import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  ViewStyle,
  Pressable,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../hooks/useTheme';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { RootStackParamList } from '../../types/navigation';
import { trackButtonClick } from '../../utils/analytics';

interface BlurredLockOverlayProps {
  children: React.ReactNode;
  message?: string;
  style?: ViewStyle;
  /** When true, uses lighter blur and more transparent overlay so content (e.g. colors) is partially visible */
  partial?: boolean;
  /** Override blur amount in px. When set, used with overlayOpacity for aligned blur across public screens. */
  blurAmount?: number;
  /** Override overlay opacity 0-1. When set with blurAmount, aligns blur across public screens. */
  overlayOpacity?: number;
}

/**
 * Wraps content with a blurred overlay and lock icon for public preview mode.
 * Click the lock to sign up. Hover shows "Unlock Now" on web.
 */
const BlurredLockOverlay: React.FC<BlurredLockOverlayProps> = ({
  children,
  message = 'Sign up to unlock!',
  style,
  partial = false,
  blurAmount,
  overlayOpacity: overlayOpacityProp,
}) => {
  const { colors } = useTheme();
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const [isHovered, setIsHovered] = useState(false);

  const handlePress = () => {
    trackButtonClick('Unlock Now', 'lock-overlay', { source: 'blurred_lock' });
    navigation.navigate('Register');
  };

  const overlayOpacity = overlayOpacityProp ?? (partial ? 0.35 : 0.85);
  const blurPx = blurAmount ?? (partial ? 2 : 8);
  const contentBlur =
    Platform.OS === 'web'
      ? {
          filter: `blur(${blurPx}px)`,
          // @ts-expect-error - web-only style
          WebkitFilter: `blur(${blurPx}px)`,
        }
      : {};

  const goColor = colors.success;

  const lockContent = (
    <>
      <View
        style={[
          styles.lockBadge,
          {
            backgroundColor: colors.border + 'CC',
            borderRadius: 24,
          },
        ]}
      >
        <Ionicons
          name="lock-closed"
          size={24}
          color={isHovered ? goColor : colors.text}
        />
      </View>
      <Text style={[styles.message, { color: colors.text }]}>
        {Platform.OS === 'web' && isHovered ? 'Unlock Now' : message}
      </Text>
    </>
  );

  return (
    <View style={[styles.container, partial && styles.containerPartial, style]}>
      <View
        style={[
          styles.contentWrapper,
          partial && styles.contentWrapperPartial,
          Platform.OS === 'web' && contentBlur,
        ]}
      >
        {children}
      </View>
      <View
        pointerEvents="box-none"
        style={[
          styles.overlay,
          {
            backgroundColor:
              Platform.OS === 'web'
                ? `rgba(255, 255, 255, ${overlayOpacity})`
                : `rgba(255, 255, 255, ${overlayOpacity})`,
          },
        ]}
      >
        <Pressable
          style={[
            styles.lockButton,
            Platform.OS === 'web' && { cursor: 'pointer' },
          ]}
          onPress={handlePress}
          onHoverIn={() => setIsHovered(true)}
          onHoverOut={() => setIsHovered(false)}
        >
          {lockContent}
        </Pressable>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 12,
  },
  containerPartial: {
    overflow: 'visible',
  },
  contentWrapper: {
    minHeight: 60,
  },
  contentWrapperPartial: {
    overflow: 'visible',
    width: '100%',
    alignSelf: 'stretch',
  },
  blurredContent: Platform.select({
    web: {
      filter: 'blur(8px)',
      // @ts-expect-error - web-only style
      WebkitFilter: 'blur(8px)',
    },
    default: {},
  }),
  overlay: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  lockButton: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  lockBadge: {
    width: 48,
    height: 48,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  message: {
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'center',
  },
});

export default BlurredLockOverlay;
